<template>
  <div class="automation-configure-signal-settings-component" v-bind:class="{loaded, loading}">
    <div v-if="!loaded">
      <a-form-model-item label="Beknopte omschrijving" :required="true">
        <Skeleton class="skeleton" v-bind:loading="true" style="width: 100%; height: 32px;"></Skeleton>
      </a-form-model-item>
      <a-form-model-item label="Notitie toevoegen" :required="true">
        <Skeleton class="skeleton" v-bind:loading="true" style="width: 100%; height: 115px;"></Skeleton>
      </a-form-model-item>
      <a-form-model-item label="Toewijzen aan gebruiker(s)" :required="true">
        <Skeleton class="skeleton" v-bind:loading="true" style="width: 100%; height: 32px;"></Skeleton>
      </a-form-model-item>
      <a-form-model-item label="Toewijzen aan afdeling(en)" :required="true">
        <Skeleton class="skeleton" v-bind:loading="true" style="width: 100%; height: 32px;"></Skeleton>
      </a-form-model-item>
    </div>
    <div v-else>
      <a-form-model-item label="Beknopte omschrijving" :required="true" :has-feedback="true"
                         :validate-status="hasValidationErrors('signal_title')"
                         :help="getValidationMessage('signal_title')">
        <a-input class="signal-input-content mt4" style="width: 100%;" :disabled="loading"
                 v-bind:value="signalTitle"
                 placeholder="Naam van het signaal"
                 @change="onContentChange"/>
      </a-form-model-item>

      <a-form-model-item label="Notitie toevoegen" :required="true" :has-feedback="true"
                         :validate-status="hasValidationErrors('signal_description')"
                         :help="getValidationMessage('signal_description')">
        <a-textarea class="signal-input-description mt4" :required="true" style="width: 100%;" :disabled="loading"
                    @change="onDescriptionChange"
                    v-bind:value="signalDescription" placeholder="Beschrijving"
                    :rows="5"/>
      </a-form-model-item>

      <a-form-model-item label="Toewijzen aan gebruiker(s)" :required="doesntHaveSelectedDepartments"
                         :has-feedback="true"
                         :validate-status="hasValidationErrors('signal_assigned_users')"
                         :help="getValidationMessage('signal_assigned_users')">
        <users-select class="signal-input-users mt4" style="width: 100%;" :disabled="loading"
                      v-bind:value="assignedUsers"
                      @change="onUsersChange"/>
      </a-form-model-item>

      <a-form-model-item label="Toewijzen aan afdeling(en)" :required="doesntHaveSelectedUsers" :has-feedback="true"
                         :validate-status="hasValidationErrors('signal_assigned_user_departments')"
                         :help="getValidationMessage('signal_assigned_user_departments')">
        <user-departments-select class="signal-input-departments mt4" :disabled="loading"
                                 v-bind:value="assignedUserDepartments"
                                 @change="onUserDepartmentsChange"/>
      </a-form-model-item>

      <a-alert show-icon :type="doesntHaveSelectedDepartments && doesntHaveSelectedUsers ? 'warning' : 'success'"
               style="margin-top: 1.5em;">
        <template v-slot:message>
          Selecteer tenminste één; gebruiker of afdeling.
        </template>
      </a-alert>

      <p style="margin-top: 1.5em;">
        <a-switch v-model="signalVisibleForCustomer" v-on:change="onSignalVisibleForCustomerChange"
                  class="mr-md"/>
        Zichtbaar voor klant
      </p>

      <a-form-model-item v-if="signalVisibleForCustomer" label="Beschrijving voor klant" :required="true"
                         :has-feedback="true"
                         :validate-status="hasValidationErrors('signal_customer_description')"
                         :help="getValidationMessage('signal_customer_description')">
        <a-textarea class="signal-input-description mt4" :required="true" style="width: 100%;" :disabled="loading"
                    @change="onSignalCustomerDescriptionChange"
                    v-bind:value="signalCustomerDescription" placeholder="Beschrijving voor klant"
                    :rows="2"/>
      </a-form-model-item>

      <p style="margin-top: 1.5em;">
        <a-switch v-model="signalAutoProcess" class="mr-md"/>
        Signalen automatisch verwerken
      </p>

      <a-form-model-item v-if="signalAutoProcess" label="Automatisch verwerken na" :required="true"
                         :has-feedback="true"
                         :validate-status="hasValidationErrors('signal_process_after')"
                         :help="getValidationMessage('signal_process_after')">
        <a-select v-model="signalAutoProcessAfter">
          <a-select-option v-for="days in 30" :value="days" :key="days">
            <template v-if="days === 1">1 dag</template>
            <template v-else>{{ days }} dagen</template>
          </a-select-option>
        </a-select>
      </a-form-model-item>

    </div>
  </div>
</template>

<script>
import { Skeleton } from "vue-loading-skeleton";
import store from "../store";
import { mapActions, mapGetters } from "vuex";

export default {
  name: "AutomationConfigureSignalSettingsComponent",
  components: {
    Skeleton
  },
  props: {
    value: {
      type: Object,
      required: false,
      default: () => {
        return {};
      },
    },
  },
  data() {
    return {
      form: {},
      loaded: false,
      loading: false,
    };
  },
  mounted() {
    const STORE_KEY = "$_automation";
    // eslint-disable-next-line no-underscore-dangle
    if (!(STORE_KEY in this.$store._modules.root._children)) {
      this.$store.registerModule(STORE_KEY, store);
    }

    Promise.all([
      this.fetchUsers(),
      this.fetchUserDepartments(),
      this.fetchMe(),
    ])
        .then()
        .catch()
        .finally(() => {
          this.loading = false;
          this.loaded = true;
        });
  },
  computed: {
    ...mapGetters('$_automation', [
      'users',
      'userDepartments',
      'me',
    ]),
    ...mapGetters('$_form_page', [
      'errorsObject',
    ]),
    assignedUsers: {
      get() {
        return this.value.signal_assigned_users;
      }
    },
    assignedUserDepartments: {
      get() {
        return this.value.signal_assigned_user_departments;
      }
    },
    doesntHaveSelectedDepartments() {
      return this.assignedUserDepartments === null
          || this.assignedUserDepartments === undefined
          || this.assignedUserDepartments.length === 0;
    },
    doesntHaveSelectedUsers() {
      return this.assignedUsers === null
          || this.assignedUsers === undefined
          || this.assignedUsers.length === 0;
    },
    signalTitle: {
      get() {
        return this.value.signal_title;
      }
    },
    signalDescription: {
      get() {
        return this.value.signal_description;
      }
    },
    signalVisibleForCustomer: {
      get() {
        return Boolean(this.value.signal_visible_for_customer) || false;
      },
      set(value) {
        this.$emit("input", Object.assign({}, this.value, { signal_visible_for_customer: value }));
      }
    },
    signalAutoProcess: {
      get() {
        return Boolean(this.value.signal_auto_process);
      },
      set(value) {
        this.$emit("input", Object.assign({}, this.value, { signal_auto_process: value }));
      }
    },
    signalAutoProcessAfter: {
      get() {
        return this.value.signal_process_after;
      },
      set(value) {
        this.$emit("input", Object.assign({}, this.value, { signal_process_after: value }));
      }
    },
    signalCustomerDescription: {
      get() {
        return this.value.signal_customer_description
      }
    },
  },
  methods: {
    ...mapActions('$_automation', {
      fetchCategories: "fetchCategories",
      fetchUsers: "fetchUsers",
      fetchUserDepartments: "fetchUserDepartments",
      fetchTaskTemplates: "fetchTaskTemplates",
      fetchMe: "fetchMe",
    }),
    onContentChange(event) {
      this.$emit("input", Object.assign({}, this.value, { signal_title: event.target.value }));
      this.clearErrorsByName('signal_title');
    },
    onDescriptionChange(event) {
      this.$emit("input", Object.assign({}, this.value, { signal_description: event.target.value }));
      this.clearErrorsByName('signal_description');
    },
    // Implement the onChange methods for the users, departments, labels, date and category fields
    onUsersChange(value) {
      this.$emit("input", Object.assign({}, this.value, { signal_assigned_users: value }));
      this.clearSignalAssignedToValidationErrors();
    },
    onUserDepartmentsChange(value) {
      this.$emit("input", Object.assign({}, this.value, { signal_assigned_user_departments: value }));
      this.clearSignalAssignedToValidationErrors();
    },
    onSignalVisibleForCustomerChange(value) {
      this.clearErrorsByName('signal_visible_for_customer');
    },
    onSignalCustomerDescriptionChange(event) {
      this.$emit("input", Object.assign({}, this.value, { signal_customer_description: event.target.value }));
      this.clearErrorsByName('signal_customer_description');
    },
    hasValidationErrors (key) {
      if (this.errorsObject !== undefined) {
        return Object.hasOwn(this.errorsObject, key) ? 'error' : null;
      }
    },
    getValidationMessage (key) {
      if (this.errorsObject !== undefined) {
        return Object.hasOwn(this.errorsObject, key) ? this.errorsObject[key][0] : null;
      }
    },
    clearErrorsByName (key){
      if ('$_form_page' in this.$store._modules.root._children) {
        this.$store.commit('$_form_page/CLEAR_ERRORS_BY_NAME', key);
      }
    },
    clearSignalAssignedToValidationErrors() {
      this.clearErrorsByName('signal_assigned_user_departments');
      this.clearErrorsByName('signal_assigned_users');
    },
  },
};
</script>

<style scoped lang="scss">
.automation-configure-signal-settings-component {
  position: relative;

  .ant-select {
    width: 100% !important;
    display: block !important;
  }
}
</style>
